import { AfterViewInit, Component, TemplateRef, ViewChild, inject, signal, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResults } from '@bo/ng-api';
import { CardTopup } from '../../../../_interfaces/card-topups.interface';
import { CardTopupsApiService } from '../../../../_services/topup-service/api/card-topups-api.service';
import { recurringTopupTableColumns } from '../../../../_services/topup-service/card-topups-utils';
import { TopUpSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { TableColumn } from '@swimlane/ngx-datatable';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { CurrencyPipe } from '@angular/common';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { RecurringFilterComponent } from './recurring-filter/recurring-filter.component';
import { updateParamsObject } from '../../../../shared/utils/filter-utils';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { TopUpFrequency, TopUpFrequencyLabel } from '../../../../_interfaces/employee.interface';
import { CardTopupsBalanceWidget } from '../_components/card-topups-balance-widget/card-topups-balance-widget';
import { statusBadgeDepartments } from '../../../../shared/components/status-badge/status-badge.component';
import { CardStatus, CardType } from '../../../../_interfaces/cards.interface';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CardNumberDisplayComponent } from '../../../../shared/components/card-number-display/card-number-display.component';

@Component({
  selector: 'app-recurring-top-ups',
  imports: [
    AppTableComponent,
    RouterOutlet,
    RecurringFilterComponent,
    EmptyIndicatorPipe,
    CurrencyPipe,
    CardTopupsBalanceWidget,
    TableHeaderTemplateComponent,
    TooltipModule,
    CardNumberDisplayComponent,
  ],
  templateUrl: './recurring-topups.component.html',
})
export class RecurringTopupsComponent implements OnInit, AfterViewInit {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly offcanvasService = inject(OffcanvasService);
  readonly cardTopupsApiService = inject(CardTopupsApiService);

  columns: Array<TableColumn> = [];
  queryParams = new HttpParams();
  protected readonly TopUpFrequency = TopUpFrequency;
  protected readonly TopUpFrequencyLabel = TopUpFrequencyLabel;
  protected readonly topUpSearchOptions = TopUpSearchOptions;
  private readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('cardNumberCell', { static: false }) cardNumberCell!: TemplateRef<any>;
  @ViewChild('cardNameCell', { static: false }) cardNameCell!: TemplateRef<any>;
  @ViewChild('cardHolderCell', { static: false }) cardHolderCell!: TemplateRef<any>;
  @ViewChild('frequencyCell', { static: false }) frequencyCell!: TemplateRef<any>;
  @ViewChild('amountCell', { static: false }) amountCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Recurring Top-ups'));
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = recurringTopupTableColumns;
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;

    for (const column of this.columns) {
      if (column.prop === 'card.masked_number') column.cellTemplate = this.cardNumberCell;
      if (column.prop === 'card.name') column.cellTemplate = this.cardNameCell;
      if (column.prop === 'employee.first_name') column.cellTemplate = this.cardHolderCell;
      if (column.prop === 'frequency') column.cellTemplate = this.frequencyCell;
      if (column.prop === 'amount') column.cellTemplate = this.amountCell;
      if (column.prop === 'actions') column.cellTemplate = this.actionsCell;
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  getData(params: HttpParams): Observable<PaginatedResults<CardTopup>> {
    return this.cardTopupsApiService.getRecurringTopups(params);
  }

  update(params: HttpParams) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: updateParamsObject(params),
    });
  }

  onCreate() {
    this.offcanvasService.offCanvasNavigate(`/card-topups/recurring/add`);
  }

  editRule(id: number) {
    this.offcanvasService.offCanvasNavigate(`/card-topups/recurring/edit/${id}`);
  }

  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly CardType = CardType;
  protected readonly CardStatus = CardStatus;
}
